/**
 * CustomDrawingController — a thin replacement for Google Maps' deprecated
 * `google.maps.drawing.DrawingManager`.
 *
 * Public API mirrors what MapView consumed:
 *   - setDrawingMode(mode | null)   where mode ∈ 'polygon' | 'polyline' | 'rectangle' | 'circle' | 'marker'
 *   - getDrawingMode()              returns the current mode (or null)
 *   - setOptions({ polygonOptions, polylineOptions, rectangleOptions, circleOptions, markerOptions })
 *   - destroy()
 *
 * Constructor options:
 *   - onOverlayComplete({ type, overlay })  fired when a shape finishes drawing
 *
 * UX:
 *   - polygon / polyline: click to add vertices, double-click to finish, ESC to cancel
 *   - rectangle / circle: mousedown + drag + mouseup
 *   - marker: single click
 *   - Map panning is disabled only while drawing a rectangle/circle (drag would conflict).
 */

import { createMarker } from './mapUtils';

const MIN_POLYGON_VERTICES = 3;
const MIN_POLYLINE_VERTICES = 2;
const SNAP_VERTEX_PX = 12; // pixel threshold for snap-to-vertex (preferred)
const SNAP_EDGE_PX = 8;    // pixel threshold for snap-to-edge (fallback)

// Hidden OverlayView used to translate LatLng → container pixels for snap calculation.
let _ProjectionHelper = null;
function _getProjectionHelper(map) {
  if (!_ProjectionHelper) {
    class P extends window.google.maps.OverlayView {
      onAdd() {}
      draw() {}
      onRemove() {}
      toPx(latLng) {
        const proj = this.getProjection();
        return proj ? proj.fromLatLngToContainerPixel(latLng) : null;
      }
    }
    _ProjectionHelper = P;
  }
  const helper = new _ProjectionHelper();
  helper.setMap(map);
  return helper;
}

// Pull a vertex array from any supported shape. Used for snap targets.
function _extractVertices(shape) {
  const g = window.google.maps;
  if (!shape) return [];
  if (shape instanceof g.Polygon || (shape instanceof g.Polyline && !(shape instanceof g.Polygon))) {
    const path = shape.getPath();
    const arr = [];
    for (let i = 0; i < path.getLength(); i++) arr.push(path.getAt(i));
    return arr;
  }
  if (shape instanceof g.Rectangle) {
    const b = shape.getBounds();
    if (!b) return [];
    const ne = b.getNorthEast(), sw = b.getSouthWest();
    return [
      new g.LatLng(sw.lat(), sw.lng()),
      new g.LatLng(ne.lat(), sw.lng()),
      new g.LatLng(ne.lat(), ne.lng()),
      new g.LatLng(sw.lat(), ne.lng()),
    ];
  }
  return [];
}

// Pull edges (consecutive vertex pairs) for snap-to-edge.
// For polygons & rectangles, edges wrap around (closed). For polylines they don't.
function _extractEdges(shape) {
  const g = window.google.maps;
  const verts = _extractVertices(shape);
  if (verts.length < 2) return [];
  const edges = [];
  for (let i = 0; i < verts.length - 1; i++) edges.push([verts[i], verts[i + 1]]);
  const isClosed = shape instanceof g.Polygon || shape instanceof g.Rectangle;
  if (isClosed && verts.length >= 3) edges.push([verts[verts.length - 1], verts[0]]);
  return edges;
}

// Foot of perpendicular from point P onto segment AB, all in 2D pixel space.
// Returns { x, y, t } where t is clamped to [0,1]; if the projection falls outside the
// segment, the result is the nearest endpoint.
function _projectPointToSegmentPx(p, a, b) {
  const ax = a.x, ay = a.y, bx = b.x, by = b.y;
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-9) return { x: ax, y: ay, t: 0 };
  let t = ((p.x - ax) * dx + (p.y - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: ax + t * dx, y: ay + t * dy, t };
}

export class CustomDrawingController {
  constructor(map, opts = {}) {
    this.map = map;
    this.onOverlayComplete = opts.onOverlayComplete || (() => {});
    // Optional: function returning an array of existing Google Maps shapes used as
    // snap targets when the user holds Shift while drawing polygons/polylines.
    this.getExistingShapes = opts.getExistingShapes || (() => []);
    this.options = {
      polygonOptions: opts.polygonOptions || {},
      polylineOptions: opts.polylineOptions || {},
      rectangleOptions: opts.rectangleOptions || {},
      circleOptions: opts.circleOptions || {},
      markerOptions: opts.markerOptions || {},
    };
    this._mode = null;
    this._listeners = [];
    this._tempShape = null;
    this._tempPath = [];
    this._isDragging = false;
    this._dragStart = null;
    this._origMapOptions = null;
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._shiftDown = false;
    this._ctrlDown = false;
    this._snapTarget = null;      // LatLng if a snap is currently active, else null
    this._snapIndicator = null;   // marker rendered at the snap target
    this._projectionHelper = null;
  }

  setOptions(opts) {
    Object.assign(this.options, opts);
  }

  getDrawingMode() {
    return this._mode;
  }

  setDrawingMode(mode) {
    if (this._mode === mode) return;
    this._teardown();
    this._mode = mode || null;
    if (!this._mode) return;
    this._setup(this._mode);
  }

  destroy() {
    this._teardown();
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    if (this._projectionHelper) {
      try { this._projectionHelper.setMap(null); } catch (e) { /* noop */ }
      this._projectionHelper = null;
    }
  }

  // ============ internals ============

  _onKeyDown(e) {
    if (e.key === 'Shift') { this._shiftDown = true; return; }
    if (e.key === 'Control' || e.key === 'Meta') { this._ctrlDown = true; return; }
    if (e.key === 'Escape' && this._mode) {
      const m = this._mode;
      this._teardown();
      this._mode = null;
      // Notify caller that drawing was cancelled (no overlay).
      try { this.onOverlayComplete({ type: m, overlay: null, cancelled: true }); } catch (err) { /* noop */ }
    }
  }

  _onKeyUp(e) {
    if (e.key === 'Shift') {
      this._shiftDown = false;
      this._clearSnap();
    }
    if (e.key === 'Control' || e.key === 'Meta') {
      this._ctrlDown = false;
    }
  }

  _setup(mode) {
    const g = window.google.maps;
    // Disable map drag for shapes that need mousedown+drag.
    if (mode === 'rectangle' || mode === 'circle') {
      this._origMapOptions = {
        draggable: this.map.get('draggable'),
        draggableCursor: this.map.get('draggableCursor'),
      };
      this.map.setOptions({ draggable: false, draggableCursor: 'crosshair' });
    } else {
      this._origMapOptions = { draggableCursor: this.map.get('draggableCursor') };
      this.map.setOptions({ draggableCursor: 'crosshair' });
    }
    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    this._shiftDown = false;

    if (mode === 'polygon' || mode === 'polyline') {
      this._setupVertexMode(mode);
    } else if (mode === 'rectangle') {
      this._setupRectangleMode();
    } else if (mode === 'circle') {
      this._setupCircleMode();
    } else if (mode === 'marker') {
      this._setupMarkerMode();
    }
  }

  _teardown() {
    const g = window.google.maps;
    this._listeners.forEach(l => g.event.removeListener(l));
    this._listeners = [];
    if (this._tempShape) {
      try { this._tempShape.setMap(null); } catch (e) { /* noop */ }
      this._tempShape = null;
    }
    if (this._vertexDots && this._vertexDots.length) {
      this._vertexDots.forEach(d => { try { d.setMap(null); } catch (e) { /* noop */ } });
      this._vertexDots = [];
    }
    this._clearSnap();
    this._tempPath = [];
    this._isDragging = false;
    this._dragStart = null;
    if (this._origMapOptions) {
      this.map.setOptions(this._origMapOptions);
      this._origMapOptions = null;
    }
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    this._shiftDown = false;
    this._ctrlDown = false;
  }

  // ===== snap helpers =====
  _clearSnap() {
    this._snapTarget = null;
    if (this._snapIndicator) {
      try { this._snapIndicator.setMap(null); } catch (e) { /* noop */ }
      this._snapIndicator = null;
    }
  }

  _ensureProjectionHelper() {
    if (!this._projectionHelper) {
      this._projectionHelper = _getProjectionHelper(this.map);
    }
    return this._projectionHelper;
  }

  _findSnap(latLng) {
    if (!this._shiftDown || !latLng) return null;
    const helper = this._ensureProjectionHelper();
    const targetPx = helper.toPx(latLng);
    if (!targetPx) return null;
    const shapes = (() => {
      try { return this.getExistingShapes() || []; } catch (e) { return []; }
    })();

    // 1) Try vertex snap (preferred — tighter threshold).
    let bestVertex = null;
    let bestVertexDist = SNAP_VERTEX_PX;
    for (const s of shapes) {
      const verts = _extractVertices(s);
      for (const v of verts) {
        const px = helper.toPx(v);
        if (!px) continue;
        const d = Math.hypot(px.x - targetPx.x, px.y - targetPx.y);
        if (d < bestVertexDist) { bestVertexDist = d; bestVertex = v; }
      }
    }
    if (bestVertex) return { latLng: bestVertex, kind: 'vertex' };

    // 2) Fall back to edge snap (perpendicular projection in pixel space).
    let bestEdgeProjPx = null;
    let bestEdgeDist = SNAP_EDGE_PX;
    let bestEdgeAB = null;
    for (const s of shapes) {
      const edges = _extractEdges(s);
      for (const [a, b] of edges) {
        const aPx = helper.toPx(a);
        const bPx = helper.toPx(b);
        if (!aPx || !bPx) continue;
        const fp = _projectPointToSegmentPx(targetPx, aPx, bPx);
        const d = Math.hypot(fp.x - targetPx.x, fp.y - targetPx.y);
        if (d < bestEdgeDist) {
          bestEdgeDist = d;
          bestEdgeProjPx = fp;
          bestEdgeAB = [a, b];
        }
      }
    }
    if (bestEdgeProjPx && bestEdgeAB) {
      // Interpolate the LatLng along the edge by fraction t (proj's t).
      const [a, b] = bestEdgeAB;
      const lat = a.lat() + (b.lat() - a.lat()) * bestEdgeProjPx.t;
      const lng = a.lng() + (b.lng() - a.lng()) * bestEdgeProjPx.t;
      return { latLng: new window.google.maps.LatLng(lat, lng), kind: 'edge' };
    }
    return null;
  }

  _updateSnap(latLng) {
    const snap = this._findSnap(latLng);
    if (!snap) { this._clearSnap(); return; }
    const fillColor = snap.kind === 'vertex' ? '#7367F0' : '#FF9F43'; // purple = vertex, orange = edge
    if (!this._snapIndicator) {
      this._snapIndicator = createMarker({
        position: snap.latLng,
        map: this.map,
        clickable: false,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor,
          fillOpacity: 0.95,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
    } else {
      try { this._snapIndicator.setPosition(snap.latLng); } catch (e) { /* noop */ }
    }
    this._snapTarget = snap.latLng;
  }

  // Right-angle constraint: when the user holds Ctrl and at least one prior edge exists,
  // project the cursor onto the line through the last vertex that is either parallel or
  // perpendicular to the previous edge (whichever is closer in pixel space). This forces
  // the next edge to be 0° or 90° relative to the previous edge.
  _applyRightAngleConstraint(cursorLatLng) {
    if (!this._ctrlDown) return cursorLatLng;
    const pts = this._tempPath || [];
    if (pts.length < 2) return cursorLatLng;
    const helper = this._ensureProjectionHelper();
    const last = pts[pts.length - 1];
    const prev = pts[pts.length - 2];
    const lastPx = helper.toPx(last);
    const prevPx = helper.toPx(prev);
    const curPx = helper.toPx(cursorLatLng);
    if (!lastPx || !prevPx || !curPx) return cursorLatLng;
    const ex = lastPx.x - prevPx.x;
    const ey = lastPx.y - prevPx.y;
    const len = Math.hypot(ex, ey);
    if (len < 0.001) return cursorLatLng;
    const ux = ex / len, uy = ey / len;
    const vx = curPx.x - lastPx.x;
    const vy = curPx.y - lastPx.y;
    const parProj = vx * ux + vy * uy;
    const perpProj = -vx * uy + vy * ux;
    let snappedPx;
    if (Math.abs(parProj) >= Math.abs(perpProj)) {
      snappedPx = { x: lastPx.x + parProj * ux, y: lastPx.y + parProj * uy };
    } else {
      snappedPx = { x: lastPx.x + perpProj * -uy, y: lastPx.y + perpProj * ux };
    }
    const proj = helper.getProjection();
    if (!proj) return cursorLatLng;
    return proj.fromContainerPixelToLatLng(new window.google.maps.Point(snappedPx.x, snappedPx.y));
  }


  _setupVertexMode(mode) {
    const g = window.google.maps;
    const isPolygon = mode === 'polygon';
    const baseOpts = isPolygon ? this.options.polygonOptions : this.options.polylineOptions;
    const strokeColor = baseOpts.strokeColor || '#7367F0';
    // The in-progress shape lives as a Polyline (open) while the user is placing
    // vertices, even for polygons — that way the segments preview accurately. We
    // upgrade to a Polygon on finish. Use a thicker, fully-opaque stroke so the
    // user can clearly see what they're drawing on satellite imagery.
    this._tempShape = new g.Polyline({
      ...baseOpts,
      strokeColor,
      strokeOpacity: 1,
      strokeWeight: 3,
      map: this.map,
      clickable: false,
      editable: false,
      path: [],
      zIndex: 1000,
    });
    this._tempPath = [];
    this._vertexDots = []; // tiny markers showing each placed vertex

    const addVertexDot = (latLng) => {
      const dot = createMarker({
        position: latLng,
        map: this.map,
        clickable: false,
        zIndex: 1001,
        icon: { path: g.SymbolPath.CIRCLE, scale: 5, fillColor: strokeColor, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 },
      });
      this._vertexDots.push(dot);
    };

    // Helper that resolves the effective cursor point: snap > right-angle > raw cursor.
    const resolveCursor = (raw) => {
      if (this._shiftDown && this._snapTarget) return this._snapTarget;
      if (this._ctrlDown) return this._applyRightAngleConstraint(raw);
      return raw;
    };

    this._listeners.push(g.event.addListener(this.map, 'mousemove', (e) => {
      this._updateSnap(e.latLng);
      // Rubber-band preview: tempShape path = committed points + resolved cursor.
      if (this._tempPath.length > 0 && this._tempShape) {
        const cursor = resolveCursor(e.latLng);
        this._tempShape.setPath([...this._tempPath, cursor]);
      }
    }));

    this._listeners.push(g.event.addListener(this.map, 'click', (e) => {
      const pt = resolveCursor(e.latLng);
      this._tempPath.push(pt);
      this._tempShape.setPath(this._tempPath);
      addVertexDot(pt);
    }));

    this._listeners.push(g.event.addListener(this.map, 'dblclick', (e) => {
      const minV = isPolygon ? MIN_POLYGON_VERTICES : MIN_POLYLINE_VERTICES;
      // Google fires two 'click' events before 'dblclick'. The 2nd click will have
      // pushed a duplicate point identical (or near-identical) to the previous one.
      // Drop it (and its visual dot).
      if (this._tempPath.length >= 2) {
        const a = this._tempPath[this._tempPath.length - 1];
        const b = this._tempPath[this._tempPath.length - 2];
        if (a && b && Math.abs(a.lat() - b.lat()) < 1e-9 && Math.abs(a.lng() - b.lng()) < 1e-9) {
          this._tempPath.pop();
          const removed = this._vertexDots.pop();
          if (removed) try { removed.setMap(null); } catch (err) { /* noop */ }
        }
      }
      if (this._tempPath.length < minV) return;
      const finalPath = [...this._tempPath];
      // Build the real shape
      const overlay = isPolygon
        ? new g.Polygon({ ...this.options.polygonOptions, paths: finalPath, map: this.map, editable: true, draggable: true })
        : new g.Polyline({ ...this.options.polylineOptions, path: finalPath, map: this.map, editable: true, draggable: true });
      this._teardown();
      this._mode = null;
      try { this.onOverlayComplete({ type: mode, overlay }); } catch (err) { /* noop */ }
    }));
  }

  _setupRectangleMode() {
    const g = window.google.maps;
    this._listeners.push(g.event.addListener(this.map, 'mousedown', (e) => {
      this._isDragging = true;
      this._dragStart = e.latLng;
      const bounds = new g.LatLngBounds(this._dragStart, this._dragStart);
      this._tempShape = new g.Rectangle({
        ...this.options.rectangleOptions,
        bounds,
        map: this.map,
        clickable: false,
      });
    }));
    this._listeners.push(g.event.addListener(this.map, 'mousemove', (e) => {
      if (!this._isDragging || !this._tempShape) return;
      const sw = new g.LatLng(
        Math.min(this._dragStart.lat(), e.latLng.lat()),
        Math.min(this._dragStart.lng(), e.latLng.lng())
      );
      const ne = new g.LatLng(
        Math.max(this._dragStart.lat(), e.latLng.lat()),
        Math.max(this._dragStart.lng(), e.latLng.lng())
      );
      this._tempShape.setBounds(new g.LatLngBounds(sw, ne));
    }));
    this._listeners.push(g.event.addListener(this.map, 'mouseup', (e) => {
      if (!this._isDragging || !this._tempShape) return;
      const finalBounds = this._tempShape.getBounds();
      // Discard tiny accidental drags
      const sw = finalBounds.getSouthWest();
      const ne = finalBounds.getNorthEast();
      const tiny = Math.abs(ne.lat() - sw.lat()) < 1e-7 && Math.abs(ne.lng() - sw.lng()) < 1e-7;
      this._tempShape.setMap(null);
      this._tempShape = null;
      this._isDragging = false;
      this._dragStart = null;
      if (tiny) return; // user clicked without dragging; stay in rectangle mode
      const overlay = new g.Rectangle({
        ...this.options.rectangleOptions,
        bounds: finalBounds,
        map: this.map,
        editable: true,
        draggable: true,
      });
      this._teardown();
      this._mode = null;
      try { this.onOverlayComplete({ type: 'rectangle', overlay }); } catch (err) { /* noop */ }
    }));
  }

  _setupCircleMode() {
    const g = window.google.maps;
    this._listeners.push(g.event.addListener(this.map, 'mousedown', (e) => {
      this._isDragging = true;
      this._dragStart = e.latLng;
      this._tempShape = new g.Circle({
        ...this.options.circleOptions,
        center: this._dragStart,
        radius: 0,
        map: this.map,
        clickable: false,
      });
    }));
    this._listeners.push(g.event.addListener(this.map, 'mousemove', (e) => {
      if (!this._isDragging || !this._tempShape) return;
      const r = g.geometry.spherical.computeDistanceBetween(this._dragStart, e.latLng);
      this._tempShape.setRadius(r);
    }));
    this._listeners.push(g.event.addListener(this.map, 'mouseup', () => {
      if (!this._isDragging || !this._tempShape) return;
      const c = this._tempShape.getCenter();
      const r = this._tempShape.getRadius();
      this._tempShape.setMap(null);
      this._tempShape = null;
      this._isDragging = false;
      this._dragStart = null;
      if (r < 0.1) return; // accidental click without drag
      const overlay = new g.Circle({
        ...this.options.circleOptions,
        center: c,
        radius: r,
        map: this.map,
        editable: true,
        draggable: true,
      });
      this._teardown();
      this._mode = null;
      try { this.onOverlayComplete({ type: 'circle', overlay }); } catch (err) { /* noop */ }
    }));
  }

  _setupMarkerMode() {
    const g = window.google.maps;
    this._listeners.push(g.event.addListener(this.map, 'click', (e) => {
      const overlay = createMarker({
        ...this.options.markerOptions,
        position: e.latLng,
        map: this.map,
        draggable: true,
      });
      this._teardown();
      this._mode = null;
      try { this.onOverlayComplete({ type: 'marker', overlay }); } catch (err) { /* noop */ }
    }));
  }
}

export default CustomDrawingController;
